
import { useState, useEffect } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { POSITION_ATTRIBUTES } from '../../staff/api/constants';

// Constants from your system
const REPORTING_TO = 'SjP47qi3JeB'; // DHIS2 attribute UID for "Reporting To"

// Helper: Convert attributes array to map
const extractAttributes = (attributes = []) => {
  const map = {};
  attributes.forEach((attr) => {
    map[attr.attribute] = attr.value;
  });
  return map;
};

// Fetch the Position TEI where CURRENT_INCUMBENT == userId
const getCurrentUserPosition = async (userId, orgUnits, engine) => {
  console.log('🔍 [APPR - GET CURRENT USER POSITION] Starting for user:', userId);
  if (!userId || !orgUnits?.length) {
    console.warn('❌ [APPR - ABORT] Missing userId or orgUnits');
    return null;
  }

  const normalizedUserId = String(userId).trim();
  console.log('📥 [APPR] Querying positions with filter:', `V66IFsw5kq6:like:${normalizedUserId}`);

  try {
    const response = await engine.query({
      positions: {
        resource: 'trackedEntityInstances',
        params: {
          trackedEntityType: 'UGGo0Ebjl0E', // Position TE type
          ouMode: 'ACCESSIBLE',
          filter: `V66IFsw5kq6:like:${normalizedUserId}`,
          fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
          pageSize: 100,
        },
      },
    });

    const allPositions = response.positions?.trackedEntityInstances || [];
    console.log(`✅ [APPR] Found ${allPositions.length} position(s) matching user ${userId}`);

    if (allPositions.length === 0) {
      console.warn('⚠️ [APPR] No positions found for user. Listing all accessible positions for inspection:');
      const allTEIs = await engine.query({
        allPositions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            ouMode: 'ACCESSIBLE',
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
            pageSize: 100,
          },
        },
      });
      const all = allTEIs.allPositions?.trackedEntityInstances || [];
      all.forEach((pos, i) => {
        const attrs = extractAttributes(pos.attributes);
        console.log(
          `   ${i + 1}. TEI: ${pos.trackedEntityInstance} | OrgUnit: ${pos.orgUnit} | Incumbent: "${attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT]}"`
        );
      });
      return null;
    }

    // Find exact match (not just "like")
    const userPosition = allPositions.find((pos) => {
      const attrs = extractAttributes(pos.attributes);
      return attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] === userId;
    });

    if (!userPosition) {
      console.warn(`❌ [APPR] No EXACT match for user ID ${userId}. Close matches:`);
      allPositions.forEach((pos, i) => {
        const attrs = extractAttributes(pos.attributes);
        console.log(`   ${i + 1}. Incumbent: "${attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT]}"`);
      });
      return null;
    }

    const attributes = extractAttributes(userPosition.attributes);
    console.log('🎯 [APPR] USER POSITION FOUND:', {
      tei: userPosition.trackedEntityInstance,
      orgUnit: userPosition.orgUnit,
      positionTitle: attributes[POSITION_ATTRIBUTES.POSITION_TITLE],
      reportingTo: attributes[REPORTING_TO],
    });

    return userPosition;
  } catch (error) {
    console.error('💥 [APPR] Error in getCurrentUserPosition:', error);
    return null;
  }
};

// Fetch manager's position
const getReportingToPosition = async (position, engine) => {
  if (!position) return null;
  const attributes = extractAttributes(position.attributes);
  const reportingToTEI = attributes[REPORTING_TO];
  if (!reportingToTEI) return null;

  try {
    const response = await engine.query({
      pos: {
        resource: `trackedEntityInstances/${reportingToTEI}`,
        params: { fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]' },
      },
    });
    return response.pos || null;
  } catch (error) {
    console.error('❌ [APPR] Error loading reporting-to position:', error);
    return null;
  }
};

// Fetch direct reports
const getDirectReports = async (position, engine) => {
  if (!position) return [];
  const tei = position.trackedEntityInstance;

  try {
    const response = await engine.query({
      reports: {
        resource: 'trackedEntityInstances',
        params: {
          trackedEntityType: 'UGGo0Ebjl0E',
          ouMode: 'ACCESSIBLE',
          filter: `${REPORTING_TO}:eq:${tei}`,
          fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
          pageSize: 100,
        },
      },
    });
    return response.reports?.trackedEntityInstances || [];
  } catch (error) {
    console.error('❌ [APPR] Error loading direct reports:', error);
    return [];
  }
};

// Main hook
export const useCurrentUserPosition = (userId, orgUnits = []) => {
  const [state, setState] = useState({
    currentUserPosition: null,
    reportingToPosition: null,
    directReportPositions: [],
    loading: true,
  });

  const engine = useDataEngine();

  useEffect(() => {
    if (!userId || !orgUnits.length) {
      console.warn('⚠️ [APPR HOOK] Missing userId or orgUnits — skipping');
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    let isMounted = true;

    const load = async () => {
      try {
        const position = await getCurrentUserPosition(userId, orgUnits, engine);
        if (!isMounted) return;

        if (!position) {
          setState({
            currentUserPosition: null,
            reportingToPosition: null,
            directReportPositions: [],
            loading: false,
          });
          return;
        }

        const [reportingTo, directReports] = await Promise.all([
          getReportingToPosition(position, engine),
          getDirectReports(position, engine),
        ]);

        if (isMounted) {
          setState({
            currentUserPosition: position,
            reportingToPosition: reportingTo,
            directReportPositions: directReports,
            loading: false,
          });
        }
      } catch (error) {
        console.error('💥 [APPR HOOK] Fatal error:', error);
        if (isMounted) {
          setState((s) => ({ ...s, loading: false }));
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [userId, JSON.stringify(orgUnits)]); // Safe dependency for array

  return state;
};